import { DEFAULT_VOLUME, isVolume } from "./audio/volume";
import { FULL_NECK, FretWindow, isFretWindow } from "./fretWindow";
import { PITCH_CLASS_COUNT } from "./library/notes";
import { OVERLAY_KINDS, OverlayKind } from "./library/overlays";
import { ThemeMode } from "./theme";

const STORAGE_KEY = "globalSettings";

export interface GlobalSettingsState {
  themeMode: ThemeMode;
  overlayKind: OverlayKind;
  overlayRootPitchClass: number;
  fretWindow: FretWindow;
  letRing: boolean;
  volume: number;
}

export const DEFAULTS: GlobalSettingsState = {
  themeMode: "light",
  overlayKind: "none",
  overlayRootPitchClass: 0,
  fretWindow: FULL_NECK,
  letRing: false,
  volume: DEFAULT_VOLUME,
};

const isThemeMode = (value: unknown): value is ThemeMode => value === "light" || value === "dark";
const isOverlayKind = (value: unknown): value is OverlayKind =>
  typeof value === "string" && (OVERLAY_KINDS as string[]).includes(value);
const isPitchClass = (value: unknown): value is number =>
  Number.isInteger(value) && (value as number) >= 0 && (value as number) < PITCH_CLASS_COUNT;
const isBoolean = (value: unknown): value is boolean => typeof value === "boolean";

function pick<T>(value: unknown, isValid: (value: unknown) => value is T, fallback: T): T {
  return isValid(value) ? value : fallback;
}

/** Each field validated on its own, so one bad value costs only that field, not the rest. */
export function parseSettings(raw: string | null): GlobalSettingsState {
  if (raw === null) return DEFAULTS;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return DEFAULTS;
  }

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return DEFAULTS;
  }

  const obj = parsed as Record<string, unknown>;
  return {
    themeMode: pick(obj.themeMode, isThemeMode, DEFAULTS.themeMode),
    overlayKind: pick(obj.overlayKind, isOverlayKind, DEFAULTS.overlayKind),
    overlayRootPitchClass: pick(
      obj.overlayRootPitchClass,
      isPitchClass,
      DEFAULTS.overlayRootPitchClass
    ),
    fretWindow: pick(obj.fretWindow, isFretWindow, DEFAULTS.fretWindow),
    letRing: pick(obj.letRing, isBoolean, DEFAULTS.letRing),
    volume: pick(obj.volume, isVolume, DEFAULTS.volume),
  };
}

export function loadSettings(): GlobalSettingsState {
  return parseSettings(localStorage.getItem(STORAGE_KEY));
}

export function saveSettings(state: GlobalSettingsState): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}
